<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <div
        class="
          d-flex
          justify-content-between
          align-items-center
          content-sidebar-header
          px-2
          py-1
        "
      >
        <h5 class="mb-0 text-capitalize">
          {{ isAdd ? `${$t("actions.add")} ${$t("vxg_master_account.title")}` :`${$t("actions.edit")} ${$t("vxg_master_account.title")}` }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <validation-provider
            #default="validationContext"
            name="name"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('vxg_master_account.name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="description"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('vxg_master_account.description')"
              label-for="description"
            >
              <b-form-input
                id="description"
                v-model="itemData.description"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="api_server"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('vxg_master_account.api_server')"
              label-for="api_server"
            >
              <b-form-input
                id="api_server"
                v-model="itemData.api_server"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="api_user"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('vxg_master_account.api_user')"
              label-for="api_user"
            >
              <b-form-input
                id="api_user"
                v-model="itemData.api_user"
                :state="getValidationState(validationContext)"
                trim
                autocomplete="username"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="api_password"
            :rules="passwordRules"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('vxg_master_account.api_password')"
              label-for="api_password"
            >
              <b-input-group>
                <b-form-input
                  id="api_password"
                  v-model="itemData.api_password"
                  type="text"
                  :state="getValidationState(validationContext)"
                  trim
                  autocomplete="new-password"
                />
                <b-input-group-append>
                  <b-button
                    variant="outline-secondary"
                    :disabled="!itemData.api_password"
                    @click="copyPassword"
                  >
                    <feather-icon icon="CopyIcon" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="server_video"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('vxg_master_account.server_video')"
              label-for="server_video"
            >
              <b-form-input
                id="server_video"
                v-model="itemData.server_video"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="export_service_url"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('vxg_master_account.export_service_url')"
              label-for="export_service_url"
            >
              <b-form-input
                id="export_service_url"
                v-model="itemData.export_service_url"
                :state="getValidationState(validationContext)"
                trim
                placeholder="http://servidor:9100"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="tenant"
            rules="required"
          >
            <b-form-group
              :label="$tc('tenants.title', 2)"
              label-for="tenant"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="tenantArray"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="tenantOptions"
                :clearable="false"
                :reduce="(val) => val.value"
                input-id="tenant"
                multiple
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
             {{$t('actions.cancel')}}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BInputGroup,
  BInputGroupAppend,
  BButton,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import { required, alphaNum, email, positive } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import i18n from "@/libs/i18n";

function buildBasePayload(itemDataValue, tenantIds) {
  return {
    name: itemDataValue.name,
    description: itemDataValue.description,
    api_server: itemDataValue.api_server,
    api_user: itemDataValue.api_user,
    api_password: itemDataValue.api_password,
    server_video: itemDataValue.server_video,
    export_service_url: itemDataValue.export_service_url,
    tenants: tenantIds,
  };
}

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    tenantOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    const tenantArray = ref([]);
    const blankData = {
      name: null,
      description: null,
      api_server: null,
      api_user: null,
      api_password: null,
      server_video: null,
      export_service_url: null,
    };

    const toast = useToast();

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      tenantArray.value = [];
    };

    const passwordRules = computed(() => (props.isAdd ? "required" : ""));

    const onSubmit = () => {
      const tenantIds = tenantArray.value;
      let dispatch;

      if (props.isAdd) {
        dispatch = store.dispatch(
          "securos_master_account/add",
          buildBasePayload(itemData.value, tenantIds)
        );
      } else {
        const params = buildBasePayload(itemData.value, tenantIds);
        if (!params.api_password) {
          delete params.api_password;
        }
        dispatch = store.dispatch("securos_master_account/edit", {
          id: itemData.value.id,
          params,
        });
      }

      dispatch
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const copyPassword = () => {
      if (!itemData.value.api_password) return;
      navigator.clipboard.writeText(itemData.value.api_password);
      toast({
        component: ToastificationContent,
        props: {
          title: i18n.t("messages.copy.copied_successfully"),
          icon: "ClipboardIcon",
          variant: "success",
        },
      });
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetData);

    const loadData = () => {
      tenantArray.value = [];
      if (!props.isAdd) {
        itemData.value = {
          ...JSON.parse(JSON.stringify(blankData)),
          ...props.itemEdit,
          api_password: props.itemEdit.api_password ?? "",
        };
        tenantArray.value =
          props.itemEdit.tenants?.map((tenant) => tenant.id) ?? [];
        return;
      }
      itemData.value = JSON.parse(JSON.stringify(blankData));
      tenantArray.value = [];
    };

    return {
      itemData,
      onSubmit,
      tenantArray,
      passwordRules,
      copyPassword,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
    };
  },
};
</script>
